import { request } from '.';
import { Id } from 'src/utils/types';

export type AttendanceStatus = 'scheduled' | 'completed' | 'cancelled' | 'no_show';

export type AttendanceFilters = {
  company_id?: Id;
  psychologist_id?: Id;
  status?: AttendanceStatus | '';
  unit?: string;
  sector?: string;
  start_date?: string;
  end_date?: string;
  service_type?: string;
};

export type AttendanceSummary = {
  total: number;
  completed: number;
  scheduled: number;
  cancelled: number;
  no_show: number;
  taxa_comparecimento: number;
  taxa_cancelamento: number;
  taxa_falta: number;
  colaboradores_unicos: number;
  psicologos_ativos: number;
};

export type AttendanceTimelineItem = {
  date: string;
  scheduled: number;
  completed: number;
  cancelled: number;
  no_show: number;
};

export type AttendanceStatusDistributionItem = {
  status: AttendanceStatus;
  total: number;
};

export type AttendanceByCompanyItem = {
  company_id: number;
  company_name: string | null;
  total: number;
  completed: number;
  cancelled: number;
  no_show: number;
};

export type AttendanceByPsychologistItem = {
  psychologist_id: number;
  psychologist_name: string | null;
  completed: number;
  cancelled: number;
  no_show: number;
};

export type AttendanceLogbookItem = {
  date: string;
  company_id: number;
  company_name: string | null;
  unit: string | null;
  scheduled: number;
  completed: number;
  cancelled: number;
  no_show: number;
  attendance_rate: number;
  observacao: string;
};

export type AttendanceFilterOptions = {
  companies: { id: number; name: string | null }[];
  psychologists: { id: number; name: string | null }[];
  units: string[];
  sectors: string[];
  service_types: string[];
};

const toParams = (filters: AttendanceFilters): Record<string, string> => {
  const params: Record<string, string> = {};
  Object.entries(filters).forEach(([key, value]) => {
    if (value !== undefined && value !== null && value !== '') {
      params[key] = String(value);
    }
  });
  return params;
};

export const getAttendanceSummary = async (
  filters: AttendanceFilters
): Promise<AttendanceSummary> => {
  const { data } = await request.get<AttendanceSummary>(
    '/dashboard/attendance/summary',
    { params: toParams(filters) }
  );
  return data;
};

export const getAttendanceTimeline = async (
  filters: AttendanceFilters
): Promise<AttendanceTimelineItem[]> => {
  const { data } = await request.get<AttendanceTimelineItem[]>(
    '/dashboard/attendance/timeline',
    { params: toParams(filters) }
  );
  return data;
};

export const getStatusDistribution = async (
  filters: AttendanceFilters
): Promise<AttendanceStatusDistributionItem[]> => {
  const { data } = await request.get<AttendanceStatusDistributionItem[]>(
    '/dashboard/attendance/status-distribution',
    { params: toParams(filters) }
  );
  return data;
};

export const getAttendanceByCompany = async (
  filters: AttendanceFilters
): Promise<AttendanceByCompanyItem[]> => {
  const { data } = await request.get<AttendanceByCompanyItem[]>(
    '/dashboard/attendance/by-company',
    { params: toParams(filters) }
  );
  return data;
};

export const getAttendanceByPsychologist = async (
  filters: AttendanceFilters
): Promise<AttendanceByPsychologistItem[]> => {
  const { data } = await request.get<AttendanceByPsychologistItem[]>(
    '/dashboard/attendance/by-psychologist',
    { params: toParams(filters) }
  );
  return data;
};

export const getAttendanceLogbook = async (
  filters: AttendanceFilters
): Promise<AttendanceLogbookItem[]> => {
  const { data } = await request.get<AttendanceLogbookItem[]>(
    '/dashboard/attendance/logbook',
    { params: toParams(filters) }
  );
  return data;
};

export const getAttendanceFilterOptions = async (
  filters: AttendanceFilters
): Promise<AttendanceFilterOptions> => {
  const { data } = await request.get<AttendanceFilterOptions>(
    '/dashboard/attendance/filters',
    { params: toParams(filters) }
  );
  return data;
};

export const ATTENDANCE_STATUS_LABELS: Record<AttendanceStatus, string> = {
  scheduled: 'Marcados',
  completed: 'Realizados',
  cancelled: 'Cancelados',
  no_show: 'Faltas',
};
